import LeadForm from "@/components/LeadForm";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { CheckCircle2, ShieldCheck, Phone } from "lucide-react";

const WHATSAPP_URL =
  "https://wa.me/48512929641?text=" +
  encodeURIComponent("Dzień dobry, mam pytanie o dzierżawę nieruchomości pod maszt BTS.");

const benefits = [
  "Stabilny, wieloletni dochód z nieużywanej nieruchomości",
  "Umowa na 10 lat z możliwością przedłużenia do 29 lat",
  "Wszystkie koszty i formalności po naszej stronie",
  "Zero inwestycji ze strony właściciela",
];

const stats = [
  { value: "do 29", label: "Lat — maksymalny okres umowy" },
  { value: "2030", label: "Cel pokrycia 5G w Polsce" },
  { value: "24h", label: "Czas naszej reakcji" },
  { value: "0 zł", label: "Twoja inwestycja" },
];

export default function HeroSection() {
  return (
    <section
      id="hero"
      className="relative min-h-dvh flex flex-col justify-center overflow-hidden bg-[#0A0E1A]"
      data-testid="hero-section"
    >
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.55) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.55) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "radial-gradient(ellipse 80% 70% at 50% 40%, #000 40%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 80% 70% at 50% 40%, #000 40%, transparent 100%)",
        }}
      />
      <div
        className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-emerald-500/20 blur-[120px] pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-32 -right-24 w-[460px] h-[460px] rounded-full bg-sky-500/15 blur-[120px] pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="hidden lg:block absolute top-24 right-[6%] w-[260px] h-[260px] opacity-25 pointer-events-none"
        aria-hidden="true"
      >
        <svg viewBox="0 0 260 260" className="w-full h-full">
          <g fill="none" stroke="#34D399" strokeWidth="1.25">
            <circle cx="130" cy="130" r="36" opacity="0.55" />
            <circle cx="130" cy="130" r="68" opacity="0.4" />
            <circle cx="130" cy="130" r="100" opacity="0.28" />
            <circle cx="130" cy="130" r="128" opacity="0.18" />
          </g>
          <circle cx="130" cy="130" r="4" fill="#34D399" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 pt-28 pb-20">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 text-xs sm:text-sm font-medium px-4 py-1.5 rounded-full mb-6 backdrop-blur-sm">
              <span className="relative flex w-2 h-2">
                <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              Aktywnie poszukujemy działek w całej Polsce
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight mb-6"
              data-testid="hero-headline"
            >
              Zamień nieużywaną{" "}
              <span className="text-emerald-400">ziemię</span> w pewny pasywny
              dochód
            </h1>

            <p className="text-lg text-slate-100 leading-relaxed mb-8 max-w-xl">
              Dynamiczny rozwój sieci 5G wymaga tysięcy nowych lokalizacji w
              całej Polsce. Dzierżawimy grunty i dachy pod maszty BTS —
              zajmujemy się wszystkim, Ty czerpiesz długoterminowy dochód.
            </p>

            <ul className="space-y-3 mb-10">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-slate-100">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="text-sm sm:text-base">{b}</span>
                </li>
              ))}
            </ul>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6 border-t border-white/15">
              {stats.map((s) => (
                <div key={s.label} className="text-center sm:text-left">
                  <div className="text-2xl sm:text-3xl font-black text-emerald-400 number-highlight">
                    {s.value}
                  </div>
                  <div className="text-xs text-slate-300 font-medium mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div id="hero-form" data-testid="hero-form">
            <div className="bg-white/[0.97] backdrop-blur-xl rounded-3xl p-7 sm:p-8 shadow-2xl shadow-emerald-500/20 ring-1 ring-white/20">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4 text-emerald-600" aria-hidden="true" />
                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">
                  Bezpłatna wycena
                </span>
              </div>
              <h2 className="text-2xl font-black text-slate-900 mb-1 tracking-tight">
                Sprawdź potencjał swojej działki
              </h2>
              <p className="text-slate-600 text-sm mb-6">
                Bez zobowiązań. Odpowiadamy w ciągu 24 godzin.
              </p>
              <LeadForm />
            </div>

            <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-sm">
              <a
                href="tel:+48512929641"
                className="flex items-center justify-center gap-2 text-white/90 hover:text-white font-medium group transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded-md py-2 px-3"
              >
                <Phone className="w-4 h-4 text-emerald-400 group-hover:text-emerald-300" aria-hidden="true" />
                <span>Zadzwoń: </span>
                <span className="font-bold text-emerald-300 group-hover:text-emerald-200 tabular-nums">
                  512&nbsp;929&nbsp;641
                </span>
              </a>
              <span className="text-white/30 hidden sm:inline" aria-hidden="true">·</span>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-white/90 hover:text-white font-medium group transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded-md py-2 px-3"
              >
                <WhatsAppIcon className="w-4 h-4 text-emerald-400 group-hover:text-emerald-300" />
                <span>Napisz na <span className="font-bold text-emerald-300 group-hover:text-emerald-200">WhatsApp</span></span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
